import React, { useState, useEffect } from 'react';
import { User } from '../../services/authService';
import { cashflowService, CashflowEntry } from '../../services/cashflowService';
import './Cashflow.css';

interface CashflowListProps {
  user: User | null;
  onEdit: (entry: CashflowEntry) => void;
  onDelete: (id: string) => void;
}

interface GroupedEntry {
  date: string;
  total: number;
  entries: CashflowEntry[];
}

const CashflowList: React.FC<CashflowListProps> = ({ user, onEdit, onDelete }) => {
  const [groupedEntries, setGroupedEntries] = useState<GroupedEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showMenu, setShowMenu] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      loadCashflow();
    }
  }, [user]);

  const loadCashflow = async () => {
    try {
      setLoading(true);
      const response = await cashflowService.getCashflowEntries({
        limit: 100,
        exclude_categories: ['income', 'investment']
      });

      if (response.success && response.cashflow) {
        // Group entries by date
        const grouped = groupByDate(response.cashflow);
        setGroupedEntries(grouped);
      }
    } catch (error) {
      console.error('Error loading cashflow:', error);
    } finally {
      setLoading(false);
    }
  };

  const groupByDate = (entries: CashflowEntry[]): GroupedEntry[] => {
    const grouped: { [key: string]: CashflowEntry[] } = {};
    
    entries.forEach(entry => {
      const date = new Date(entry.created_at).toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric'
      });
      
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(entry);
    });

    return Object.keys(grouped)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
      .map(date => {
        const entries = grouped[date];
        const total = entries.reduce((sum, entry) => sum + Math.abs(entry.amount), 0);
        return { date, total, entries };
      });
  };

  const formatAmount = (amount: number): string => {
    return Math.abs(amount).toLocaleString('en-US');
  };

  const handleMenuClick = (e: React.MouseEvent, entryId: string) => {
    e.stopPropagation();
    setShowMenu(showMenu === entryId ? null : entryId);
  };

  const handleEditClick = (entry: CashflowEntry) => {
    setShowMenu(null);
    onEdit(entry);
  };

  const handleDeleteClick = (entryId: string) => {
    setShowMenu(null);
    onDelete(entryId);
  };

  if (loading) {
    return <div className="cashflow-loading">Loading...</div>;
  }

  return (
    <div className="cashflow-list">
      {groupedEntries.length === 0 ? (
        <div className="cashflow-empty">No cashflow entries found</div>
      ) : (
        groupedEntries.map(group => (
          <div key={group.date} className="cashflow-date-group">
            <div className="cashflow-date-header">
              <span className="cashflow-date-label">{group.date}</span>
              <span className="cashflow-date-total">{formatAmount(group.total)}</span>
            </div>
            {group.entries.map(entry => (
              <div key={entry.id} className="cashflow-entry">
                <div className="cashflow-entry-main">
                  <span className="cashflow-entry-amount">{formatAmount(entry.amount)}</span>
                  <span className="cashflow-entry-description">{entry.notes || entry.category || 'No description'}</span>
                </div>
                <div className="cashflow-entry-menu-container">
                  <button
                    className="cashflow-entry-menu"
                    onClick={(e) => handleMenuClick(e, entry.id)}
                  >
                    ⋮
                  </button>
                  {showMenu === entry.id && (
                    <div className="cashflow-entry-menu-dropdown">
                      <button onClick={() => handleEditClick(entry)}>Edit</button>
                      <button onClick={() => handleDeleteClick(entry.id)}>Delete</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))
      )}
      {showMenu && (
        <div 
          className="cashflow-menu-overlay" 
          onClick={() => setShowMenu(null)}
        />
      )}
    </div>
  );
};

export default CashflowList;
